import { ChatboxPosition, Crisp } from "crisp-sdk-web";
import { PropsWithChildren, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import $api from "../queries/openapi";
import CrispContext, { ContextValue } from "./CrispContext";
import "./styles.css";

export default function CrispProvider({ children }: PropsWithChildren) {
  const { teamSlug } = useParams();
  // When a user logs in or out all the query client data is invalidated, so we don't need to do anything special to pull this again
  const { data: crispData } = $api.useQuery("get", "/api/crisp/init");

  Crisp.setPosition(ChatboxPosition.Left);

  useEffect(() => {
    if (!crispData) {
      return;
    }

    Crisp.configure(crispData.crisp_website_id, {
      tokenId: crispData.user?.email_signature ?? undefined,
      sessionMerge: true,
    });

    if (crispData.user) {
      if (!crispData.user.email.endsWith(".test")) {
        // Crisp will throw an error since .test isn't a valid TLD

        Crisp.user.setEmail(
          crispData.user.email,
          crispData.user.email_signature ?? undefined,
        );
      }
      Crisp.user.setNickname(crispData.user.nickname);
      Crisp.user.setAvatar(crispData.user.avatar);

      if (teamSlug) {
        Crisp.user.setCompany(teamSlug, {});
      }
    } else {
      Crisp.session.reset();
    }

    Crisp.setTokenId(crispData.user?.email_signature ?? undefined);
  }, [crispData, teamSlug]);

  const [isChatOpened, setChatOpened] = useState(false);

  // Use the Crisp widget as the source of truth for React state
  useEffect(() => {
    Crisp.chat.onChatOpened(() => setChatOpened(true));
    Crisp.chat.onChatClosed(() => setChatOpened(false));
  }, []);

  const contextValue: ContextValue = useMemo(
    () => ({
      crisp: Crisp,
      isChatOpened,
    }),
    [isChatOpened],
  );

  return (
    <CrispContext.Provider value={contextValue}>
      {children}
    </CrispContext.Provider>
  );
}
